<script>
import { mapGetters } from 'vuex';
import { getSidebarItems } from './config/default-sidebar';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRoute, useRouter } from 'vue-router';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import { routesWithPermissions } from '../../routes';
import {
  getUserPermissions,
  hasPermissions,
} from '../../helper/permissionsHelper';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isMobileSidebarOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleAccountModal',
    'showAddLabelPopup',
    'openNotificationPanel',
    'closeKeyShortcutModal',
    'openKeyShortcutModal',
    'closeMobileSidebar',
  ],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const { accountId } = useAccount();

    const toggleKeyShortcutModal = () => {
      emit('openKeyShortcutModal');
    };
    const closeKeyShortcutModal = () => {
      emit('closeKeyShortcutModal');
    };
    const isCurrentRouteSameAsNavigation = routeName => {
      return route.name === routeName;
    };
    const navigateToRoute = routeName => {
      if (!isCurrentRouteSameAsNavigation(routeName)) {
        router.push({ name: routeName });
      }
    };
    const keyboardEvents = {
      '$mod+Slash': {
        action: toggleKeyShortcutModal,
      },
      '$mod+Escape': {
        action: closeKeyShortcutModal,
      },
      'Alt+KeyC': {
        action: () => navigateToRoute('home'),
      },
      'Alt+KeyV': {
        action: () => navigateToRoute('contacts_dashboard'),
      },
      'Alt+KeyR': {
        action: () => navigateToRoute('account_overview_reports'),
      },
      'Alt+KeyS': {
        action: () => navigateToRoute('agent_list'),
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      toggleKeyShortcutModal,
      accountId,
    };
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnHichatCloud: 'globalConfig/isOnHichatCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
    }),
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      if (this.activePrimaryMenu.key === 'orders') {
        return 'order_entity';
      }
      return '';
    },
    customViews() {
      if (!this.activeCustomView) {
        return [];
      }
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations' ||
        this.activePrimaryMenu.key === 'orders'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        const isAvailableForTheUser = hasPermissions(
          routesWithPermissions[menuItem.toStateName],
          userPermissions
        );

        if (!isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnHichatInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
    hasSecondaryMenu() {
      return (
        this.activeSecondaryMenu.menuItems &&
        this.activeSecondaryMenu.menuItems.length
      );
    },
    hasSecondarySidebar() {
      return this.showSecondarySidebar && this.hasSecondaryMenu;
    },
    hasSecondarySidebarForMobile() {
      return this.hasSecondaryMenu;
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  async mounted() {
    await this.$store.dispatch('cacheKeys/load');

    await Promise.all([
      this.$store.dispatch('inboxes/get'),
      this.$store.dispatch('teams/get'),
      this.$store.dispatch('notifications/unReadCount'),
    ]);

    this.$nextTick(() => {
      this.$store.dispatch('labels/get');
      this.$store.dispatch('attributes/get');
      this.fetchCustomViews();
    });
  },

  methods: {
    fetchCustomViews() {
      if (this.activeCustomView) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleSupportChatWindow() {
      window.$hichat.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggleAccountModal');
    },
    showAddLabelPopup() {
      this.$emit('showAddLabelPopup');
    },
    openNotificationPanel() {
      this.$emit('openNotificationPanel');
    },
    closeMobileSidebar() {
      this.$emit('closeMobileSidebar');
    },
  },
};
</script>

<template>
  <!-- Mobile overlay backdrop -->
  <div
    v-if="isMobileView && isMobileSidebarOpen && hasSecondarySidebarForMobile"
    class="fixed inset-0 z-[55] dark:bg-black bg-opacity-50"
    @click="closeMobileSidebar"
  />

  <aside v-if="!isMobileView" class="flex h-full">
    <PrimarySidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      :is-mobile-view="false"
      @toggle-accounts="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @open-notification-panel="openNotificationPanel"
    />
    <SecondarySidebar
      v-if="hasSecondarySidebar"
      :account-id="accountId"
      :inboxes="inboxes"
      :labels="labels"
      :teams="teams"
      :custom-views="customViews"
      :menu-config="activeSecondaryMenu"
      :current-user="currentUser"
      :is-on-hichat-cloud="isOnHichatCloud"
      :is-mobile-view="false"
      @add-label="showAddLabelPopup"
      @toggle-accounts="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
    />
  </aside>

  <template v-if="isMobileView">
    <!-- Mobile Secondary Sidebar Overlay -->
    <aside
      v-if="hasSecondarySidebarForMobile"
      class="fixed top-0 bottom-20 left-0 w-[85%] max-w-[85%] sm:w-[49%] sm:right-auto z-[60] transform transition-transform duration-300 ease-in-out bg-white dark:bg-slate-900"
      :class="{
        // chừa đáy: bottom-16 (64px) mobile, bottom-20 (80px) tablet
        'bottom-[4.375rem] sm:bottom-[5.25rem]': true,
        '-translate-x-full': !isMobileSidebarOpen,
        'translate-x-0': isMobileSidebarOpen,
      }"
    >
      <div class="w-full h-full bg-white dark:bg-slate-900">
        <SecondarySidebar
          :account-id="accountId"
          :inboxes="inboxes"
          :labels="labels"
          :teams="teams"
          :custom-views="customViews"
          :menu-config="activeSecondaryMenu"
          :current-user="currentUser"
          :is-on-hichat-cloud="isOnHichatCloud"
          :is-mobile-view="true"
          @add-label="showAddLabelPopup"
          @toggle-accounts="toggleAccountModal"
          @close-sidebar="closeMobileSidebar"
          @open-key-shortcut-modal="toggleKeyShortcutModal"
        />
      </div>
    </aside>

    <!-- Mobile Primary Sidebar at bottom -->
    <div class="fixed bottom-0 left-0 right-0 z-[70]">
      <PrimarySidebar
        :logo-source="globalConfig.logoThumbnail"
        :installation-name="globalConfig.installationName"
        :is-a-custom-branded-instance="isACustomBrandedInstance"
        :account-id="accountId"
        :menu-items="primaryMenuItems"
        :active-menu-item="activePrimaryMenu.key"
        :is-mobile-view="true"
        :is-mobile-bottom-bar="true"
        @toggle-accounts="toggleAccountModal"
        @open-key-shortcut-modal="toggleKeyShortcutModal"
        @open-notification-panel="openNotificationPanel"
      />
    </div>
  </template>
</template>
